import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { getPlaces, getPlace, deleteProfile } from '../api/api';

const ProfileEditorBasicInfo = ({ 
  firstName, 
  setFirstName, 
  lastName, 
  setLastName, 
  role, 
  setRole,
  homeClub,
  setHomeClub,
  token,
  domain,
  onProfileLogout
}) => {
  const { t } = useTranslation();
  const [places, setPlaces] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  
  useEffect(() => {
    const loadPlaces = async () => {
      try {
        var placesData = await getPlaces(domain);
        if (homeClub && !placesData.find(x=>x._id === homeClub)) {
            var homePlace = await getPlace(homeClub);
            if (homePlace) {
              placesData = [...placesData, homePlace ]
            }
        }
        
        placesData.sort((a, b) => a.name.localeCompare(b.name));
        setPlaces(placesData);
      } catch (error) {
        console.error('Error loading places:', error);
      }
    };
    loadPlaces();
  }, [token, domain]);

  const handleDeleteAccount = async () => {
    try {
      await deleteProfile(token);
      setDeleteConfirmOpen(false);
      setDeleteSuccessOpen(true);
    } catch (error) {
      console.error('Error deleting profile:', error);
      setDeleteConfirmOpen(false);
      setDeleteErrorOpen(true);
    }
  };

  const handleSuccessClose = async () => {
    setDeleteSuccessOpen(false);
    await onProfileLogout();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: isMobile ? '100%' : '90%' }}>
   

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{t('profile.basicInfo.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('profile.basicInfo.deleteConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteSuccessOpen}
        onClose={handleSuccessClose}
      >
        <DialogTitle>{t('profile.basicInfo.deleteSuccessTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('profile.basicInfo.deleteSuccessMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessClose} color="primary" variant="contained">
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteErrorOpen}
        onClose={() => setDeleteErrorOpen(false)}
      >
        <DialogTitle>{t('profile.basicInfo.deleteErrorTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('profile.basicInfo.deleteErrorMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteErrorOpen(false)} color="primary" variant="contained">
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      <TextField
        fullWidth
        label={t('profile.basicInfo.firstName')}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        margin="normal"
        required
        slotProps={{ input: { maxLength: 50 } }}
      />
      <TextField
        fullWidth
        label={t('profile.basicInfo.lastName')}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        margin="normal"
        required
        slotProps={{ input: { maxLength: 50 } }}
      />
      <Autocomplete
        fullWidth
        options={places}
        getOptionLabel={(option) => option.name}
        value={places.find(place => place._id === homeClub) || null}
        onChange={(event, newValue) => {
          setHomeClub(newValue ? newValue._id : null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('profile.basicInfo.homeClub')}
            margin="normal"
            required
          />
        )}
      />
      <FormControl component="fieldset" margin="normal" required style={{ alignSelf: 'flex-start' }}>
        <FormLabel component="legend">{t('profile.basicInfo.role')}</FormLabel>
        <RadioGroup
          aria-label="role"
          name="role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <FormControlLabel value="crew" control={<Radio />} label={t('profile.basicInfo.crew')} />
          <FormControlLabel value="skipper" control={<Radio />} label={t('profile.basicInfo.skipper')} />
        </RadioGroup>
      </FormControl>
      <div style={{ 
        marginTop: '20px', 
        alignSelf: 'flex-start',
        fontSize: '0.875rem',
        display: 'flex',
        gap: '5px'
      }}>
        <span>{t('profile.basicInfo.noLongerInterested')}</span>
        <a 
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setDeleteConfirmOpen(true);
          }}
          style={{ 
            color: '#d32f2f',
            textDecoration: 'none'
          }}
        >
          {t('profile.basicInfo.deleteYourAccount')}
        </a>
      </div>
    </div>
  );
};

export default ProfileEditorBasicInfo;
